<template>
    <div 
        v-if="visible && announcements && announcements.length" 
        class="fixed inset-0 z-[1000] flex items-center justify-center bg-black/60"
    >
        <div 
            @click.stop  
            ref="modalBox"
            class="relative z-50 flex flex-col max-w-[480px] w-[90%] sm:min-h-[300px] max-h-[90%] !bg-[#2c2b2b] !text-white rounded-xl transition-all duration-300"
        >
            <div
                class="p-2 absolute z-50 top-0 w-full rounded-tr-xl transition-opacity duration-300 rounded-tl-xl to-transparent h-[58px]"
                :class="showProgress ? 'opacity-100' : 'opacity-0'"
                style="background-image: linear-gradient(rgb(0 0 0 / 80%), rgba(38, 38, 38, 0))"
            >
                <div class="flex gap-1">
                    <div v-for="(_, i) in announcements" :key="i" class="flex-1 h-0.5 rounded bg-[#555555] overflow-hidden">
                        <div
                            class="h-full bg-white transition-all duration-100"
                            :style="{ width: i < currentIndex ? '100%' : i === currentIndex ? progress + '%' : '0%' }"
                        ></div>
                    </div>
                </div>
                <div class="flex justify-end mt-2 gap-2 items-center">
                    <div class="flex gap-1 text-white">
                        <button v-if="!langImage(currentStory)" @click="prevStory">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-left-icon lucide-chevron-left"><path d="m15 18-6-6 6-6"/></svg>
                        </button>
                        <button v-if="timer == null" @click="startProgress">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
                                <path fill-rule="evenodd" d="M4.5 5.653c0-1.427 1.529-2.33 2.779-1.643l11.54 6.347c1.295.712 1.295 2.573 0 3.286L7.28 19.99c-1.25.687-2.779-.217-2.779-1.643V5.653Z" clip-rule="evenodd" />
                            </svg>
                        </button>
                        <button v-else @click="stopProgress">
                            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-6 h-6">
                                <path fill-rule="evenodd" d="M6.75 5.25a.75.75 0 0 1 .75-.75H9a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H7.5a.75.75 0 0 1-.75-.75V5.25Zm7.5 0A.75.75 0 0 1 15 4.5h1.5a.75.75 0 0 1 .75.75v13.5a.75.75 0 0 1-.75.75H15a.75.75 0 0 1-.75-.75V5.25Z" clip-rule="evenodd" />
                            </svg>
                        </button>
                        <button v-if="!langImage(currentStory)" @click="nextStory">
                            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-chevron-right-icon lucide-chevron-right"><path d="m9 18 6-6-6-6"/></svg>
                        </button>
                    </div>
                    <button class="text-white" @click.stop="visible = false">
                        <svg class="w-7 h-7" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>
                    </button>
                </div>
            </div>
            <!-- IMAGE BANNER -->
            <div
                @mousedown="holdStory"
                @mouseup="releaseStory"
                @touchstart="holdStory"
                @touchend="releaseStory"
                class="flex items-center justify-center h-full"
                :style="imagePercent !== null ? { maxHeight: imagePercent + '%' } : {}"
            >
                <div 
                    v-if="langImage(currentStory)"
                    class="w-full object-contain h-full bg-black" 
                    :class="showText(currentStory) ? 'rounded-tr-xl rounded-tl-xl' : 'rounded-xl'"
                >
                    <img
                        ref="imageBox"
                        @load="calculateProportions"
                        :src="$root.getImage(langImage(currentStory))"
                        class="w-full h-full object-contain cursor-pointer"
                        :class="showText(currentStory) ? 'rounded-tr-xl rounded-tl-xl' : 'rounded-xl'"
                        @click.stop="openExternalLink(currentStory)"
                    />
                </div>
                <div class="absolute h-full inset-y-0 left-0 w-1/2 cursor-pointer" @click="prevStory"></div>
                <div class="absolute h-full inset-y-0 right-0 w-1/2 cursor-pointer" @click="nextStory"></div>
            </div>
            <!-- CONTENT -->
            <div
                v-if="showText(currentStory)"
                ref="contentBox"
                class="w-full relative z-40 h-full flex flex-col rounded-lg p-4 overflow-y-auto announcement-scroll content-box"
                :style="contentPercent !== null ? { maxHeight: contentPercent + '%' } : {}"
                :class="!langImage(currentStory) ? 'pt-[70px]' : ''"
            >
                <div
                    v-if="showTitle(currentStory)"
                    class="text-base font-semibold mb-2"
                    v-html="$root.getLangText(currentStory.title)"
                ></div>
                <div
                    v-if="$root.getLangText(currentStory.content)"
                    v-html="$root.getLangText(currentStory.content)"
                ></div>
                <button
                    v-if="currentStory.extra && currentStory.extra.external_link"
                    class="mt-auto py-3 rounded-xl chat-bg text-white w-[90%] mx-auto font-semibold hover:bg-red-700 transition"
                    @click.stop="openExternalLink(currentStory)"
                >
                    {{ $root.getKeyWord('read_more') }}
                </button>
            </div>
        </div>
    </div>
</template>

<script>
import { eventBus } from '../../main';

export default {
    name: 'AnnouncementCarousel',

    data() {
        return {
            parentSectionId: null,
            currentIndex: 0,
            progress: 0,
            timer: null,
            showProgress: true,
            announcements: [],
            visible: false,
            contentPercent: null,
            imagePercent: 0,
        }
    },

    computed: {
        currentStory() {
            return this.announcements[this.currentIndex] || {}
        }
    },

    watch: {
        visible: {
            handler(val) {
                if (val) {
                    this.startProgress()
                    this.$nextTick(this.calculateProportions)
                } 
                else {
                    this.reset()
                } 
            },
            immediate: true
        },

        announcements() {
            this.announcements.map(item => {
                this.displayAnons(item);
            })
        },

        currentIndex() {
            this.$nextTick(this.calculateProportions)
        }
    },

    created() {
        eventBus.$on('modal-announcement', data => {
            this.parentSectionId = data.parent_section_id;
            this.fetch();
        });
    },

    methods: {
        calculateProportions() {
            const modal = this.$refs.modalBox
            if (!modal) return

            const contentEl = this.$refs.contentBox || null
            const imageEl = this.$refs.imageBox || null

            const modalHeight = modal.clientHeight || modal.offsetHeight || 0
            if (!modalHeight) return

            const contentHeight = contentEl ? (contentEl.scrollHeight || 0) : 0

            // try to get real rendered image height
            let imageHeight = 0
            if (imageEl) {
                imageHeight = imageEl.offsetHeight || 0

                // if browser hasn't laid out image height yet, use natural size to estimate
                if (!imageHeight && imageEl.naturalHeight && imageEl.naturalWidth) {
                const modalWidth = modal.clientWidth || modal.offsetWidth || 1
                imageHeight = Math.round(imageEl.naturalHeight * (modalWidth / imageEl.naturalWidth))
                }
            }

            // fallback: remaining space
            if (!imageHeight) {
                imageHeight = Math.max(modalHeight - contentHeight, 0)
            }

            // compute raw percents
            let cp = Math.round((contentHeight / modalHeight) * 100)
            let ip = Math.round((imageHeight / modalHeight) * 100)

            // if both are zero (very rare) -> give image all
            if (cp + ip === 0) {
                cp = 0
                ip = 100
            } else {
                // normalize so cp + ip == 100 and keep proportions
                const sum = cp + ip
                cp = Math.round((cp / sum) * 100)
                ip = 100 - cp
            }

            // commit
            this.contentPercent = cp
            this.imagePercent = ip

            // debug logs (optional)
            // console.log({ modalHeight, contentHeight, imageHeight, cp, ip })
        },

        onFinished() {
            this.visible = true
            console.log("Stories finished")
        },
        
        async fetch() {
            try {
                const res = await this.$http.get(
                    `${process.env.VUE_APP_API_URL}public/announcements?hotel_id=${this.$root.hotel.id}`
                );

                const data = res.data?.data || [];

                this.announcements = data.filter(item => this.shouldDisplay(item));

                if (this.announcements.length > 0) {
                    this.visible = true;
                }
            } catch (err) {
                console.error("Error fetching announcements:", err);
            }
        },

        shouldDisplay(result) {
            try {
                // LocalStorage key
                const localStorageIdAnons = 'display_announcement_' + result.id + result.updated_at

                // Already shown before
                if (!result.extra?.web_popup_config?.display_every_refresh) {
                    if (localStorage.getItem(localStorageIdAnons)) {
                        return false
                    }
                }

                // Only via QR code
                if (result.extra?.web_popup_config?.display_only_via_qrcode) {
                    const announcement_id = this.$root.getQueryVariable('announcement_id')
                    if (!announcement_id || parseInt(announcement_id) !== result.id) {
                        return false
                    }
                }

                // Section restrictions
                const includedSectionIds = result.extra?.web_popup_config?.include_section_ids
                if (includedSectionIds?.length > 0) {
                    if (this.parentSectionId == null && includedSectionIds.includes(null)) {
                        // ok: home page
                    } else if (this.parentSectionId == null || !includedSectionIds.includes(this.parentSectionId)) {
                        return false
                    }
                }

                // Room restrictions
                const includedRooms = result.extra?.web_popup_config?.display_only_room_numbers
                if (includedRooms) {
                    const currentRoom = this.getDefaultRoomNumber()
                    if (!currentRoom) return false

                    if (!includedRooms.split(',').includes(currentRoom)) {
                        return false
                    }
                }

                // Save localStorage to prevent repeat
                localStorage.setItem(localStorageIdAnons, true)

                return true
            } catch (e) {
                console.log(e)
                return false
            }
        },

        getDefaultRoomNumber() {
            return (localStorage.getItem('fs_room_number') != undefined) ? localStorage.getItem('fs_room_number') : '';
        },

        displayAnons(result) {    
            if (location.pathname.indexOf('/chat') > -1) {
                return;
            }
            
            try {
                var localStorageIdAnons = 'display_announcement_' + result.id + result.updated_at
                if (localStorage.getItem(localStorageIdAnons) === undefined || localStorage.getItem(localStorageIdAnons) === null)  
                {
                    var displayTime = result.extra.web_popup_config.display_time_after_visit_site;
                    var includedSectionIds = result.extra.web_popup_config.include_section_ids;
                    var includedRooms = result.extra.web_popup_config.display_only_room_numbers;
                    var displayEveryRefresh = result.extra.web_popup_config.display_every_refresh;
                    var displayOnlyViaQrCode = result.extra.web_popup_config.hasOwnProperty('display_only_via_qrcode') ? result.extra.web_popup_config.display_only_via_qrcode : false;

                    if (displayOnlyViaQrCode !== undefined && displayOnlyViaQrCode !== null && displayOnlyViaQrCode) {
                        var announcement_id = this.$root.getQueryVariable('announcement_id');
                        if (announcement_id !== undefined && announcement_id && parseInt(announcement_id) > 0 && result.id == parseInt(announcement_id)) {

                        } else {
                            console.log('Anons not right qrcode');
                            return false;
                        }
                    }

                    if (includedSectionIds !== undefined && includedSectionIds !== null && includedSectionIds !== '' && includedSectionIds.length > 0) {
                        if (this.parentSectionId == null && includedSectionIds.includes(null)) {
                            // display only home page
                        } else if (this.parentSectionId == null || !includedSectionIds.includes(this.parentSectionId)) {
                            console.log('Anons not right page via section');
                            return false;
                        }
                    }

                    if (includedRooms !== undefined && includedRooms !== null && includedRooms !== '') {
                        if (this.getDefaultRoomNumber() == '') 
                            return false;
                            
                        var includedRooms = includedRooms.split(',');
                        
                        if (includedRooms.indexOf(this.getDefaultRoomNumber()) < 0) {
                            console.log('Anons not right room number');
                            return false;
                        }
                    }

                    setTimeout(() => {
                        this.$modal.show('modal-announcements-' + result.id);
                    }, parseFloat(displayTime != null ? displayTime : 0) * 60 * 1000);
                    
                    if (displayEveryRefresh === undefined || displayEveryRefresh == null || !displayEveryRefresh)
                        localStorage.setItem(localStorageIdAnons, true);
                }
            } catch(e) {
                console.log(e);
            }
        },

        holdStory() {
            // this.stopProgress()
            this.showProgress = false
        },

        releaseStory() {
            // this.startProgress(false)
            this.showProgress = true
        },

        stopProgress() {
            clearInterval(this.timer)
            this.timer = null
        },

        langImage(story) {
            if (story?.extra?.lang_file?.[this.$root.currentLanguage]) {
                return story.extra.lang_file[this.$root.currentLanguage]
            }
            return story?.extra?.file || undefined
        },

        showTitle(story) {
            return story?.extra?.hide_title === undefined 
                || story.extra.hide_title === null 
                || story.extra.hide_title === false
        },

        showText(story) {
            const hasTitle = this.showTitle(story)
            const hasContent = this.$root.getLangText(story.content)
            const hasExternalLink = story?.extra?.external_link
            return hasTitle || hasContent || hasExternalLink
        },

        openExternalLink(story) {
            if (story?.extra?.external_link) {
                window.open(story.extra.external_link, "_blank")
            }
        },

        startProgress(reset = true) {
            if (this.timer) clearInterval(this.timer)

            if (reset) {
                this.progress = 0
            }

            this.showProgress = true
            this.timer = setInterval(() => {
                if (this.progress < 100) {
                    this.progress += 1
                } else {
                    this.nextStory()
                }
            }, 100)
        },

        nextStory(isManual = false) {
            this.stopProgress()
            if (this.currentIndex < this.announcements.length - 1) {
                this.currentIndex++
                this.startProgress(true)
            } else if (!isManual) {
                this.onFinished()
            }
        },

        prevStory() {
            this.stopProgress()
            if (this.currentIndex > 0) {
                this.currentIndex--
                this.startProgress(true)
            }
        },

        reset() {
            this.stopProgress()
            this.currentIndex = 0
            this.progress = 0
        }
    }
}
</script>

<style>
.announcement-scroll {
  scrollbar-width: thin;               /* Firefox */
  scrollbar-color: #978e8e transparent; /* Thumb / Track colors */
}

.announcement-scroll::-webkit-scrollbar {
  width: 6px;
}

.announcement-scroll::-webkit-scrollbar-track {
  background: transparent;
}

.announcement-scroll::-webkit-scrollbar-thumb {
  background-color: #978e8e; /* red thumb */
  border-radius: 9999px;     /* fully rounded */
}

.announcement-scroll::-webkit-scrollbar-thumb:hover {
  background-color: #645f5f;
}

.content-box * {
    background-color: transparent !important;
    color: white !important;
}
</style>